/**
 * @fileoverview Portuguese translations.
 * @summary Portuguese translation strings for KaraoNow frontend.
 *
 * @package KaraoNow
 * @module apps/front/src/utils/i18n/locales/pt
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
export const pt = {
  common: {
    loading: 'Carregando...',
    processing: 'Processando',
    error: 'Erro',
    success: 'Sucesso',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    save: 'Salvar',
    delete: 'Excluir',
    edit: 'Editar',
    create: 'Criar',
    back: 'Voltar',
    next: 'Próximo',
    previous: 'Anterior',
    search: 'Buscar',
    filter: 'Filtrar',
    close: 'Fechar',
    backHome: 'Voltar para casa',
    creating: 'Criando...',
  },
  auth: {
    login: 'Entrar',
    register: 'Registrar',
    logout: 'Sair',
    email: 'Email',
    password: 'Senha',
    confirmPassword: 'Confirmar senha',
    forgotPassword: 'Esqueceu a senha?',
    rememberMe: 'Lembrar-me',
    loginButton: 'Entrar',
    registerButton: 'Registrar',
    alreadyHaveAccount: 'Já tem uma conta?',
    dontHaveAccount: 'Não tem uma conta?',
    role: 'Função',
    organizer: 'Organizador',
    singer: 'Cantor',
    firstName: 'Nome',
    lastName: 'Sobrenome',
    pseudo: 'Pseudônimo',
    organization: 'Organização',
    sendVerificationEmail: 'Enviar email de verificação',
    emailVerificationSent: 'Email de verificação enviado',
    emailVerificationInstructions: 'Um email de verificação foi enviado para {{email}}. Clique no link do email para concluir seu registro.',
    backToLogin: 'Voltar ao login',
    optional: 'opcional',
    registerOrganizerTitle: 'Cadastro de Organizador',
    registerSingerTitle: 'Cadastro de Cantor',
    registerOrganizerDescription: 'Crie sua conta para organizar eventos de karaokê',
    registerSingerDescription: 'Crie sua conta para participar de eventos de karaokê',
    setPassword: 'Criar senha',
    setPasswordTitle: 'Finalizar registro',
    setPasswordDescription: 'Crie sua senha para finalizar seu registro',
    createPassword: 'Criar minha senha',
    passwordRequirements: 'Mínimo 12 caracteres, com maiúscula, minúscula, número e caractere especial',
    forgotPasswordDescription: 'Digite seu endereço de email e enviaremos um link para redefinir sua senha.',
    sendResetLink: 'Enviar link de redefinição',
    resetPasswordEmailSent: 'Email de redefinição enviado',
    resetPasswordInstructions: 'Um email de redefinição foi enviado para {{email}}. Clique no link do email para redefinir sua senha.',
    resetPassword: 'Redefinir senha',
    resetPasswordDescription: 'Crie uma nova senha para sua conta',
    resetPasswordButton: 'Redefinir minha senha',
    passwordResetSuccess: 'Senha redefinida',
    passwordResetSuccessDescription: 'Sua senha foi redefinida com sucesso. Você será redirecionado para a página de login.',
    resetLinkExpired: 'Link de redefinição expirado',
    resetLinkExpiredDescription: 'Este link de redefinição expirou (válido por 1 hora). Por favor, solicite um novo link.',
    requestNewResetLink: 'Solicitar um novo link',
    rememberPassword: 'Lembra da sua senha?',
  },
  // Organizer menu
  organizerMenu: {
    menu: 'Menu organizador',
    dashboard: 'Painel',
    venues: 'Locais',
    settings: 'Configurações',
    dataPrivacy: 'Dados Pessoais',
    subscriptions: 'Preços',
    user: 'Usuário',
  },
  errors: {
    genericMessage: 'Ocorreu um erro',
    required: 'Este campo é obrigatório',
    invalidEmail: 'Email inválido',
    passwordTooShort: 'A senha deve ter pelo menos 12 caracteres',
    passwordRequirements: 'A senha deve conter pelo menos uma letra maiúscula, uma minúscula, um dígito e um caractere especial',
    passwordsDoNotMatch: 'As senhas não coincidem',
    loginFailed: 'Falha no login',
    registerFailed: 'Falha no registro',
    resetPasswordFailed: 'Falha na redefinição de senha',
    invalidToken: 'Token inválido ou expirado',
    networkError: 'Erro de rede',
    unauthorized: 'Não autorizado',
    forbidden: 'Acesso negado',
    notFound: {
      title: 'Página não encontrada',
      description: 'A página que você procura não existe ou foi movida.',
      explore: 'Explorar o aplicativo',
    },
    server: {
      title: 'Erro do servidor',
      description: 'Ocorreu um erro inesperado. Tente novamente mais tarde.',
    },
    generic: {
      title: 'Ops, algo deu errado',
      description: 'Ocorreu um erro inesperado. Tente novamente.',
    },
    serverError: 'Erro do servidor',
    // Error codes from backend
    VALIDATION_ERROR: 'Erro de validação',
    CREATE_EVENT_ERROR: 'Erro ao criar o evento',
    UPDATE_EVENT_ERROR: 'Erro ao atualizar o evento',
    EVENT_NOT_FOUND: 'Evento não encontrado',
    VENUE_NOT_FOUND: 'Local não encontrado',
    QUOTA_EXCEEDED_CREATE: 'Quota mensal atingida para criação de eventos',
    QUOTA_EXCEEDED_DATE_CHANGE: 'Cota do plano {{planName}} atingida para este mês: Máximo {{limit}} evento(s) por mês. Sua cota será redefinida no final do período de faturamento.',
    SUBSCRIPTION_EVENT_CANNOT_CONVERT: 'Os eventos incluídos no seu plano de assinatura não podem ser convertidos em evento único',
    EVENT_DATE_NOT_REACHED: 'Não é possível ativar o evento antes da data programada',
    EVENT_DATE_PAST: 'A data do evento não pode estar no passado',
    EVENT_DATE_AFTER_EXPIRATION: 'A data do evento não pode ser posterior à data de expiração do seu evento único',
    // Validation errors by field and Zod code
    validation: {
      name: {
        too_small: 'O nome do evento é obrigatório',
        required: 'O nome do evento é obrigatório',
        invalid_type: 'O nome do evento é inválido',
      },
      date: {
        required: 'A data é obrigatória',
        invalid_type: 'A data é inválida',
        invalid_date: 'A data é inválida',
        invalid_string: 'O formato da data é inválido',
      },
      description: {
        invalid_type: 'A descrição é inválida',
      },
      venueId: {
        invalid_type: 'O local é inválido',
      },
    },
  },
  // Landing page
  landing: {
    languageLabel: 'Escolha o seu idioma',
    heroTitle: 'As suas noites de karaoke, simplificadas.',
    heroSubtitle: 'Crie o seu evento em 2 minutos. Os convidados escaneiam um código QR, adicionam as músicas — você mantém o controlo da noite.',
    ctaPrimary: 'Criar o meu primeiro karaoke — grátis',
    ctaOrganize: 'Quero organizar',
    ctaOrganizeDesc: 'Crie eventos, gerencie a playlist, acompanhe as atuações.',
    ctaSing: 'Quero cantar',
    ctaSingDesc: 'Junte-se a um evento via código QR e adicione as suas músicas.',
    alreadyAccount: 'Já tem conta?',
    login: 'Entrar',
    whyTitle: 'Porquê KaraoNow?',
    benefit1Title: 'Um QR code, zero papel',
    benefit1Desc: 'Os seus convidados escaneiam e escolhem as músicas no telemóvel. Chega de listas perdidas.',
    benefit2Title: 'Playlist em direto',
    benefit2Desc: 'Ordem de passagem visível para todos. Avança nas músicas sem complicações.',
    benefit3Title: 'Grátis para começar',
    benefit3Desc: 'Até 4 eventos grátis. Passe a ilimitado quando quiser.',
    twoWaysTitle: 'Duas formas de viver KaraoNow',
    learnMore: 'Saber mais',
  },
  home: {
    title: 'Gestão de playlists para eventos de Karaokê',
    wantToSing: 'Quero cantar',
    wantToSingDescription: 'Junte-se a eventos e adicione suas músicas',
    wantToOrganize: 'Quero organizar',
    wantToOrganizeDescription: 'Crie e gerencie seus eventos de karaokê',
    alreadyHaveAccount: 'Já tem uma conta?',
  },
  dashboard: {
    title: 'Painel do Organizador',
    welcome: 'Bem-vindo',
    events: 'Eventos',
    createEvent: 'Criar evento',
    noEvents: 'Nenhum evento',
    noEventsDescription: 'Comece criando seu primeiro evento de karaokê',
    filterAll: 'Todos',
    filterDraft: 'Rascunhos',
    filterActive: 'Ativos',
    filterCompleted: 'Concluídos',
    filterCancelled: 'Cancelados',
    loadingEvents: 'Carregando eventos...',
    errorLoadingEvents: 'Erro ao carregar eventos',
    noEventsMatchingFilter: 'Nenhum evento corresponde a este filtro',
    tryDifferentFilter: 'Tente selecionar um filtro diferente',
  },
  events: {
    name: 'Evento',
    events: 'Eventos',
    song: 'Música',
    songs: 'Músicas',
    description: 'Descrição',
    date: 'Data',
    time: 'Hora',
    venue: 'Local',
    selectVenue: 'Selecionar um local',
    namePlaceholder: 'Minha noite de karaokê',
    descriptionPlaceholder: 'Descrição opcional...',
    createTitle: 'Criar Evento',
    createSubtitle: 'Crie um novo evento de karaokê',
    createButton: 'Criar Evento',
    createEvent: 'Criar Evento',
    createSuccess: 'Evento criado com sucesso',
    createError: 'Erro ao criar o evento',
    noVenues: 'Nenhum local disponível. Por favor, crie um local primeiro.',
    createVenue: 'Criar Local',
    status: 'Status',
    statusDraft: 'Rascunho',
    statusActive: 'Ativo',
    statusCompleted: 'Concluído',
    statusCancelled: 'Cancelado',
    statusHidden: 'Oculto',
    createdAt: 'Criado em',
    updatedAt: 'Atualizado em',
    view: 'Ver',
    open: 'Abrir',
    edit: 'Editar',
    delete: 'Excluir',
    qrCode: 'Código QR',
    deleteConfirmTitle: 'Excluir Evento',
    deleteConfirm: 'Tem certeza de que deseja excluir o evento "{{name}}" ?',
    deleteSuccess: 'Evento excluído com sucesso',
    deleteError: 'Erro ao excluir o evento',
    managementTitle: 'Gestão do Evento',
    editEvent: 'Editar Evento',
    updateSuccess: 'Evento atualizado com sucesso',
    updateError: 'Erro ao atualizar o evento',
    showQRCode: 'Mostrar Código QR',
    hideQRCode: 'Ocultar Código QR',
    qrCodeTitle: 'Código QR do Evento',
    qrCodeDescription: 'Compartilhe este código com seus participantes para que possam se juntar ao evento',
    copyQRCode: 'Copiar Link',
    qrCodeCopied: 'Link copiado para a área de transferência',
    shareQRCode: 'Compartilhar',
    queue: 'Fila',
    singers: 'Cantores',
    noQueue: 'Nenhuma música na fila',
    noSingers: 'Nenhum cantor registrado',
    guest: 'Convidado',
    singer: 'Cantor',
    songsCount_one: '{{count}} música',
    songsCount_other: '{{count}} músicas',
    deleteQueueItem: 'Excluir música',
    deleteQueueItemConfirm: 'Tem certeza de que deseja excluir esta música da fila?',
    deleteSinger: 'Remover cantor',
    deleteSingerConfirm: 'Tem certeza de que deseja remover este cantor? Todas as suas músicas na fila também serão removidas.',
    singerDeleteError: 'Erro ao remover o cantor',
    moveUp: 'Mover para cima',
    moveDown: 'Mover para baixo',
    dragToReorder: 'Arrastar para reorganizar',
    setCurrent: 'Definir como atual',
    markCompleted: 'Marcar como concluído',
    cancel: 'Cancelar',
    queueUpdateError: 'Erro ao atualizar a fila',
    queueDeleteError: 'Erro ao excluir a música',
    queueStatusPending: 'Pendente',
    queueStatusCurrent: 'Atual',
    queueStatusCompleted: 'Concluído',
    queueStatusCancelled: 'Cancelado',
    qrCodeUrl: 'URL do evento',
    qrCodeShareText: 'Junte-se a este evento de karaokê: {{eventName}}!',
    qrCodePrintTitle: 'Código QR - KaraoNow',
    qrCodePrintInstructions: 'Escaneie este código QR para acessar o evento e adicionar suas músicas à fila.',
    qrCodePrint: 'Imprimir',
    qrCodeShare: 'Compartilhar',
    qrCodeDownload: 'Baixar',
    startEvent: 'Iniciar o evento',
    start: 'Iniciar',
    completeEvent: 'Completar o evento',
    complete: 'Completar',
    cancelEvent: 'Cancelar o evento',
    cancelConfirmTitle: 'Confirmar cancelamento',
    cancelConfirmMessage: 'Tem certeza de que deseja cancelar este evento? Esta ação é definitiva.',
    cancelConfirmOneShotTitle: 'Confirmar cancelamento - Evento único',
    cancelConfirmOneShotMessage: '⚠️ Atenção: Este evento único foi comprado. O cancelamento é definitivo e nenhum reembolso será efetuado se o evento não tiver sido utilizado. Tem certeza de que deseja cancelar este evento?',
    cancelConfirmButton: 'Sim, cancelar',
    cancelCancelButton: 'Não, manter o evento',
    deleteEvent: 'Excluir o evento',
    deleteEventConfirm: 'Tem certeza de que deseja excluir este evento? Esta ação é irreversível.',
    hide: 'Ocultar',
    hideConfirmTitle: 'Ocultar evento',
    hideConfirm: 'Ocultar o evento "{{name}}" ? Ele não será mais visível na sua lista principal, mas continuará contando na sua cota mensal.',
    hideSuccess: 'Evento ocultado com sucesso',
    hideError: 'Erro ao ocultar o evento',
    hidden: 'Oculto',
    showHidden: 'Ocultos',
    hiddenEventTitle: 'Evento oculto',
    hiddenEventMessage: 'Este evento foi ocultado e não está mais acessível. Ainda conta na sua cota mensal.',
    hiddenEventTooltip: 'Este evento está oculto e não pode ser visualizado',
    statusUpdated: 'Status do evento atualizado',
    backToEvent: 'Voltar ao evento',
    // Quota exceeded for date change
    quotaExceededDateChange: 'Cota mensal atingida para esta data',
    quotaExceededDateChangeTitle: 'Cota mensal atingida',
    quotaExceededDateChangeExplanation: 'Você não pode alterar a data do evento "{{eventName}}" pois isso excederia sua cota mensal de eventos.',
    quotaExceededDateChangeOptions: 'Você pode manter a data original ou converter este evento em "Evento Único" para removê-lo da sua cota mensal.',
    quotaExceededDetails: 'Você tem {{current}} evento(s) de {{limit}} para este período.',
    upgradeToOneShotOption: 'Opção: Passar para Evento Único',
    upgradeToOneShotDescription: 'Ao converter este evento em "Evento Único", ele não contará mais na sua cota mensal e você poderá alterar sua data livremente.',
    upgradeToOneShotNow: '🚀 Passar para Evento Único',
    keepOriginalDate: 'Manter a data original',
    multipleEventsPremiumSuggestion: 'Vários eventos? O plano Premium pode ser mais econômico.',
    // Quota exceeded for event creation
    quotaExceededCreateTitle: 'Cota mensal atingida',
    quotaExceededCreateMessage: 'Você atingiu o limite do seu {{planName}}. Você não pode criar mais eventos para este período.',
    quotaExceededCreateExplanation: 'Sua cota mensal de eventos está atingida. Você pode aguardar o final do período de faturamento ou passar para um plano superior.',
    quotaExceededCreateOptions: 'Você também pode criar um evento único que não contará na sua cota mensal.',
    // Quota exceeded for FREE plan (no monthly/billing period references)
    quotaExceededCreateTitleFree: 'Cota atingida',
    quotaExceededCreateMessageFree: 'Você atingiu o limite do seu {{planName}}. Você não pode criar mais eventos.',
    quotaExceededCreateExplanationFree: 'Você atingiu o limite de eventos do seu plano gratuito. Você pode passar para um plano superior para criar mais eventos.',
    quotaExceededCreateOptionsFree: 'Você também pode criar um evento único que não contará na sua cota.',
    quotaExceededDetailsFree: 'Você tem {{current}} evento(s) de {{limit}}.',
    createOneShotDescriptionFree: 'Crie um evento único que não contará na sua cota.',
    upgradePlanDescriptionFree: 'Passe para um plano superior para aumentar sua cota de eventos.',
    createOneShotOption: 'Opção: Criar um Evento Único',
    createOneShotDescription: 'Crie um evento único que não contará na sua cota mensal.',
    createOneShotNow: '🚀 Criar um Evento Único',
    upgradePlanOption: 'Opção: Passar para o plano superior',
    upgradePlanDescription: 'Passe para um plano superior para aumentar sua cota mensal de eventos.',
    upgradePlanNow: 'Passar para o plano superior',
    quotaResetInfo: 'Sua cota será redefinida no final do período de faturamento.',
    // Generic quota exceeded message
    quotaExceeded: 'Cota mensal atingida para esta data',
    // Plan subscription badge
    planSubscription: 'Incluído no plano',
    planSubscriptionTooltip: 'Evento incluído na sua assinatura',
  },
  // Venues
  venues: {
    title: 'Gestão de Locais',
    subtitle: 'Crie e gerencie seus locais de karaokê',
    venuesList: 'Lista de Locais',
    addVenue: 'Adicionar Local',
    editVenue: 'Editar Local',
    noVenues: 'Nenhum local registrado',
    noVenuesDescription: 'Comece criando seu primeiro local',
    eventName: 'Nome do evento',
    eventNamePlaceholder: 'Karaokê de {{firstName}}',
    name: 'Nome do Local',
    namePlaceholder: 'Ex: Bar Le Chat Noir',
    address: 'Endereço',
    addressPlaceholder: 'Ex: Rua da Música 123',
    city: 'Cidade',
    cityPlaceholder: 'Ex: São Paulo',
    country: 'País',
    countryPlaceholder: 'Ex: Brasil',
    googleMapsUrl: 'Link do Google Maps',
    googleMapsUrlPlaceholder: 'https://maps.google.com/...',
    viewOnGoogleMaps: 'Ver no Google Maps',
    addSuccess: 'Local criado com sucesso',
    addError: 'Erro ao criar o local',
    updateSuccess: 'Local atualizado com sucesso',
    updateError: 'Erro ao atualizar o local',
    deleteSuccess: 'Local excluído com sucesso',
    deleteError: 'Erro ao excluir o local',
    confirmDelete: 'Tem certeza de que deseja excluir o local "{{name}}" ?',
    confirmDeleteTitle: 'Excluir Local',
    cancel: 'Cancelar',
    add: 'Adicionar',
    edit: 'Editar',
    save: 'Salvar',
    deleting: 'Excluindo...',
    saving: 'Salvando...',
    generateMapsUrl: 'Gerar link do Google Maps',
    generateMapsUrlError: 'Preencha pelo menos o nome do local ou a cidade para gerar o link',
    mapsModal: {
      title: 'Selecionar localização no Google Maps',
      description: 'Use o Google Maps para encontrar e ajustar com precisão a localização do seu local.',
      instructions: 'Clique em "Abrir no Google Maps" abaixo, ajuste o ponto no mapa se necessário, depois copie o link da barra de endereços do navegador e cole-o no campo abaixo.',
      urlLabel: 'Link do Google Maps',
      openInMaps: 'Abrir no Google Maps',
      confirm: 'Confirmar',
      clickToOpen: 'Clique para abrir o Google Maps',
      clickDescription: 'Uma nova janela será aberta com o Google Maps para que você possa ajustar a localização',
    },
  },
  // Singer interface
  singer: {
    // Dashboard
    dashboard: 'Painel de controle',
    dashboardSubtitle: 'Visualize seus eventos e músicas',
    myEvents: 'Meus eventos',
    mySongs: 'Minhas músicas',
    allEvents: 'Todos',
    activeEvents: 'Ativos',
    pastEvents: 'Passados',
    inactiveEvents: 'Inativos',
    sortByDate: 'Ordenar por data',
    sortByOrganizer: 'Ordenar por organizador',
    sortBySongs: 'Ordenar por número de músicas',
    noEvents: 'Você ainda não participou de nenhum evento.',
    noSongs: 'Você ainda não adicionou nenhuma música.',
    eventName: 'Nome do evento',
    organizer: 'Organizador',
    date: 'Data',
    songsAdded: 'Músicas adicionadas',
    songsSung: 'Músicas cantadas',
    status: 'Status',
    status_active: 'Ativo',
    status_completed: 'Concluído',
    status_cancelled: 'Cancelado',
    status_draft: 'Rascunho',
    title: 'Título',
    artist: 'Artista',
    event: 'Evento',
    addedDate: 'Data de adição',
    totalEvents: 'Eventos totais',
    totalSongs: 'Músicas totais',
    uniqueOrganizers: 'Organizadores únicos',
    // Settings
    settings: 'Configurações',
    settingsSubtitle: 'Gerencie seu perfil e seus dados',
    menu: 'Menu do cantor',
    profile: 'Perfil',
    changePassword: 'Alterar senha',
    changePasswordDescription: 'Enviaremos um link para redefinir sua senha por email.',
    sendResetLink: 'Enviar link de redefinição',
    passwordResetLinkSent: 'Um link de redefinição de senha foi enviado para seu endereço de email.',
    accountData: 'Dados da conta',
    editProfile: 'Editar meu perfil',
    saveChanges: 'Salvar mudanças',
    profileUpdated: 'Perfil atualizado com sucesso',
    changePasswordTitle: 'Alterar minha senha',
    currentPassword: 'Senha atual',
    newPassword: 'Nova senha',
    confirmNewPassword: 'Confirmar nova senha',
    passwordRequirements: 'A senha deve conter pelo menos 12 caracteres, uma letra maiúscula, uma minúscula, um número e um caractere especial.',
    passwordChanged: 'Senha alterada com sucesso',
    passwordTooShort: 'A senha deve conter pelo menos 12 caracteres',
    passwordsNotMatch: 'As senhas não correspondem',
    currentPasswordRequired: 'Senha atual é obrigatória',
    // Erros de validação de senha
    passwordMinLength: 'A senha deve conter pelo menos 12 caracteres',
    passwordNoUppercase: 'A senha deve conter pelo menos uma letra maiúscula',
    passwordNoLowercase: 'A senha deve conter pelo menos uma letra minúscula',
    passwordNoDigit: 'A senha deve conter pelo menos um dígito',
    passwordNoSpecialChar: 'A senha deve conter pelo menos um caractere especial',
    exportData: 'Exportar meus dados',
    exportDataDescription: 'Baixe uma cópia de todos os seus dados pessoais em formato JSON.',
    downloadData: 'Baixar meus dados',
    dataExported: 'Dados exportados com sucesso',
    dangerZone: 'Zona de perigo',
    deleteAccountDescription: 'Excluir sua conta é irreversível. Todos os seus dados serão excluídos permanentemente.',
    deleteAccount: 'Excluir minha conta',
    deleteAccountConfirm: 'Excluir',
    confirmDeleteAccount: 'Tem certeza de que deseja excluir sua conta? Esta ação é irreversível.',
    // QR Code Landing
    qrCodeMissing: 'Token do código QR ausente',
    qrCodeError: 'Erro ao validar o código QR',
    eventNotFound: 'Evento não encontrado ou token inválido',
    organizerNotAllowed: 'Você é organizador. Use seu painel para gerenciar este evento.',
    accessDenied: 'Acesso não autorizado',
    validatingQRCode: 'Validando código QR...',
    chooseAccessMethod: 'Escolha como participar do evento',
    continueAsGuest: 'Continuar como convidado',
    orWithAccount: 'ou com uma conta',
    enterPseudo: 'Digite seu apelido para participar do evento',
    joinEvent: 'Participar do evento',
    joining: 'Conectando...',
    sessionError: 'Erro ao criar a sessão',
    joinError: 'Erro ao participar do evento',
    sessionExpired: 'Sua sessão expirou. Por favor, escaneie o código QR do evento novamente.',
    guestMode: 'Modo convidado',
    leaveEvent: 'Sair do evento',
    // Singer Event Page
    eventNotActive: 'Este evento ainda não está ativo. Volte mais tarde!',
    eventNotStarted: 'Este evento ainda não começou. Estará disponível quando o organizador iniciar o karaokê!',
    eventNotYetAvailable: 'Este evento ainda não está disponível. Por favor, aguarde até a data programada.',
    eventEnded: 'Este evento terminou. Os votos não são mais aceitos.',
    eventCancelled: 'Este evento foi cancelado. Os votos não estão disponíveis.',
    searchSong: 'Buscar uma música',
    searchResults: 'Resultados da pesquisa',
    advancedSearch: 'Busca avançada',
    advancedSearchToggle: 'Busca avançada',
    simpleSearch: 'Busca simples',
    searchPlaceholder: 'Título ou artista...',
    titlePlaceholder: 'Título...',
    artistPlaceholder: 'Artista...',
    noResults: 'Nenhum resultado encontrado',
    addCustomSong: 'Adicionar este título',
    addCustomSongDescription: 'Este título será salvo no banco de dados e adicionado à sua fila.',
    addAndQueue: 'Adicionar e colocar na fila',
    localResults: '{{count}} resultado(s) encontrado(s) em nossa base',
    musicbrainzResults: '{{count}} resultado(s) do MusicBrainz',
    moreResults: 'Mais resultados',
    addError: 'Erro ao adicionar à fila',
    queueLimitReached: 'Limite atingido: o organizador deste evento está usando o plano gratuito que é limitado a {{limit}} música(s) máximo na fila. A fila contém atualmente {{currentCount}} música(s). Por favor, entre em contato com o organizador se desejar adicionar mais músicas.',
    songAdded: '"{{title}}" adicionado à fila!',
    myQueue: 'Minhas músicas',
    noSongsInQueue: 'Você ainda não adicionou músicas',
    fullQueue: 'Fila',
    queueEmpty: 'A fila está vazia',
    you: 'Você',
    unknown: 'Desconhecido',
    // Queue statuses
    statusPending: 'Pendente',
    statusCurrent: 'Em andamento',
    statusCompleted: 'Concluído',
    statusCancelled: 'Cancelado',
  },
  // Subscription and Plans
  subscription: {
    title: 'Planos e preços',
    subtitle: 'Escolha o plano que se ajusta às suas necessidades',
    currentPlan: 'Plano atual',
    planFree: 'Plano Gratuito',
    planOneShot: 'Evento Único',
    planPro: 'Plano Pro',
    planPremium: 'Plano Premium',
    planUltimate: 'Plano Ultimate',
    planFreeDescription: 'Perfeito para começar',
    planOneShotDescription: 'Para um evento',
    planProDescription: 'Para organizadores regulares',
    planPremiumDescription: 'Para entusiastas',
    planUltimateDescription: 'Para profissionais',
    priceFreeFree: 'Gratuito',
    priceOneShotPer: '{{price}}€ / evento',
    priceProPer: '{{price}}€ / mês',
    pricePremiumPer: '{{price}}€ / mês',
    priceUltimatePer: '{{price}}€ / mês',
    maxEvents: 'Eventos máximos',
    maxSongsPerEvent: 'Músicas por evento',
    unlimited: 'Ilimitado',
    features: 'Características',
    currentPlanBadge: 'Plano atual',
    changePlan: 'Mudar plano',
    upgradePlan: 'Atualizar para premium',
    upgradePlanButton: 'Passar para o plano superior',
    selectPlan: 'Selecionar este plano',
    monthlyQuota: 'Cota mensal',
    freeQuota: 'Cota gratuita',
    eventsUsed: '{{current}} / {{limit}} eventos',
    billingPeriod: 'Período: {{start}} - {{end}}',
    quotaReached: 'Cota mensal atingida',
    quotaReachedMessage: 'Você atingiu seu limite de eventos para este período. A cota será redefinida no final do seu período de faturamento.',
    quotaWarning: 'Cota mensal quase atingida',
    quotaWarningMessage: 'Você tem {{remaining}} evento(s) restante(s) para este período.',
    quotaInfo: 'Você tem {{remaining}} evento(s) restante(s) para este período.',
    learnMore: 'Saiba mais',
    contactSupport: 'Contatar suporte',
    freePlanNote: 'O plano gratuito é limitado a {{maxEvents}} eventos (todos os status, excluindo Evento Único) e {{maxSongsPerEvent}} músicas por evento.',
    oneShotPlanNote: 'Número ilimitado de músicas na playlist do evento. Válido por 15 dias.',
    proPlanNote: '{{maxEvents}} eventos por mês (período móvel). Músicas ilimitadas.',
    premiumPlanNote: '{{maxEvents}} eventos por mês (período móvel). Músicas ilimitadas.',
    ultimatePlanNote: 'Eventos e músicas ilimitados.',
    noEngagement: 'Sem compromisso.',
    convertEvent: 'Converter um evento',
    createEvent: 'Criar um evento',
    chooseOption: 'Escolha uma opção:',
    convertDescription: 'Selecione um evento gratuito e converta-o em um plano Evento Único.',
    createDescription: 'Crie um novo evento com o plano Evento Único.',
    noFreeEventsToConvert: 'Você não tem um evento GRATUITO disponível para converter.',
    selectFreeEvent: 'Selecione um evento GRATUITO para converter:',
    convertThisEvent: 'Converter este evento',
    createNewOneShot: 'Criar um evento Evento Único',
    newOneShotEventName: 'Novo evento único',
    createEventError: 'Erro ao criar evento',
    createOneShotEventTitle: 'Criar um evento único',
    proceedToPayment: 'Prosseguir para o pagamento',
    // Stripe checkout
    checkoutError: 'Um erro ocorreu durante o pagamento',
    paymentSuccessful: 'Pagamento bem-sucedido!',
    paymentProcessing: 'Seu pagamento está sendo processado. Você será redirecionado em breve.',
    redirectingIn5: 'Redirecionamento automático em 5 segundos...',
    backToSubscription: 'Voltar aos planos',
    successTitle: 'Pagamento confirmado',
    successSubtitle: 'Obrigado pela sua compra',
    paymentCancelled: 'Pagamento cancelado',
    checkoutCancelledMessage: 'Você cancelou o pagamento. Você pode tentar novamente a qualquer momento.',
    cancelledTitle: 'Pagamento cancelado',
    cancelledSubtitle: 'Volte aos planos para tentar novamente',
    whatHappensNext: 'O que acontece a seguir?',
    webhooksProcessing: 'Estamos processando seu pagamento',
    subscriptionActive: 'Sua assinatura estará ativa em breve',
    confirmationEmail: 'Você receberá um email de confirmação',
    // Subscription cancellation
    cancelConfirm: 'Tem certeza de que deseja cancelar sua assinatura?',
    cancelDetails: 'Você manterá o acesso ao seu plano Premium até o final do seu período de faturamento atual. Após essa data, você será rebaixado para o plano Gratuito.',
    cancelSuccess: 'Sua assinatura foi cancelada com sucesso.',
    cancelPlan: 'Cancelar',
    cancellationInProgress: 'O cancelamento da sua assinatura já está em andamento. Você será transferido para o plano Gratuito no final do seu período de cobrança.',
    cancellationInProgressTitle: 'Cancelamento em Andamento',
    cancellationInProgressMessage: 'O cancelamento da sua assinatura já está em andamento. Você será transferido para o plano Gratuito no final do seu período de cobrança atual. Nenhuma ação adicional é necessária.',
    accessUntil: 'Você terá acesso até {{date}}.',
    cancelError: 'Ocorreu um erro ao cancelar sua assinatura',
    cancelModalTitle: 'Cancelar Assinatura',
    cancelModalMessage: 'Você manterá o acesso ao seu plano Premium até o final do seu período de cobrança atual. Após essa data, você será automaticamente transferido para o plano Gratuito.',
    cancelModalConfirm: 'Sim, cancelar minha assinatura',
    endOfBillingPeriod: 'fim do período de cobrança',
    // ONE_SHOT upgrade
    upgradeToOneShot: 'Atualizar para Evento Único',
    upgradeOneShotConfirm: 'Você está prestes a atualizar o evento "{{eventName}}" para o plano Evento Único.',
    oneShotDescription: 'Este é um pagamento único para músicas ilimitadas neste evento específico.',
    price: 'Preço',
    oneTimePayment: 'Pagamento único',
    whatYouGet: 'O que você receberá:',
    unlimitedSongs: 'Músicas ilimitadas para este evento',
    noLimits: 'Sem limites de fila',
    oneShotValidity: 'Válido até a conclusão do evento',
    upgradeNow: 'Atualizar agora',
    upgradeError: 'Erro ao atualizar o evento',
    updateSuccess: 'Assinatura atualizada com sucesso para o plano {{plan}}',
    changePlanTitle: 'Alterar plano',
    changePlanSubtitle: 'Você está prestes a modificar sua assinatura',
    newPlan: 'Novo plano',
    perMonth: 'mês',
    upgradeCost: 'Custo adicional: +{{amount}}€ / mês',
    downgradeCredit: 'Crédito estimado: -{{amount}}€ / mês',
    immediateChange: 'Mudança imediata',
    immediateChangeDescription: 'A mudança de plano será efetiva imediatamente. A cobrança proporcional será calculada automaticamente pelo Stripe.',
    confirmChange: 'Confirmar mudança',
    oneShot: 'Evento único',
    oneShotTooltip: 'Assinatura evento único',
    expiresLabel: 'Expira em',
    oneShotExpirationTitle: 'Expiração do seu evento único',
    oneShotDaysRemaining: '{{count}} dia restante',
    oneShotDaysRemaining_plural: '{{count}} dias restantes',
    oneShotExpiresOn: 'Expira em {{date}}',
    oneShotExpirationWarning: '⚠️ Seu evento voltará ao plano gratuito após esta data. Certifique-se de usá-lo antes do vencimento!',
    oneShotValidityInfo: 'Esta compra é válida por {{days}} dias a partir da data de compra. Use antes do vencimento!',
    upgradeToUnlimited: '🚀 Passar para ilimitado',
    upgradeToUnlimitedNow: '🚀 Passar para ilimitado agora',
    upgradeSingleEventConfirm: 'Você está prestes a passar o evento "{{eventName}}" para ilimitado.',
    singleEventDescription: 'Trata-se de um pagamento único para músicas ilimitadas neste evento específico.',
    singleEventPlan: 'Evento único',
    multiplePremiumSuggestion: 'Vários eventos? O plano Premium pode ser mais vantajoso.',
    comparePlans: 'Comparar planos →',
    premium: 'PREMIUM',
  },
  // Plan alerts
  planAlert: {
    limitReached: 'Limite do plano atingido',
    songsLimitReached: 'Você atingiu o limite do plano gratuito ({{limit}} música(s)). Para adicionar mais músicas, assine um plano pago.',
    eventsLimitReached: 'Você atingiu o limite do plano gratuito ({{limit}} evento(s)). Para criar mais eventos, assine um plano pago.',
    upgrade: 'Assinar',
    dismissible: true,
  },
  pwa: {
    install: 'Instalar',
    installApp: 'Instalar aplicativo',
    installPrompt: 'Instale o KaraoNow para acesso rápido!',
    installed: 'Aplicativo instalado',
    offline: 'Você está offline',
    offlineMessage: 'Alguns recursos podem estar limitados.',
    updateAvailable: 'Atualização disponível',
    updateNow: 'Atualizar agora',
  },
  // About
  about: {
    title: 'Sobre o KaraoNow',
    subtitle: 'Descubra o app que transforma seus eventos de karaoke',
    introduction: 'O que é KaraoNow?',
    introductionText: 'KaraoNow é uma plataforma SaaS inovadora projetada para gerenciar seus eventos de karaoke de forma perfeita e profissional. Seja você um organizador de eventos ou um cantor apaixonado, KaraoNow fornece as ferramentas necessárias para criar uma experiência de karaoke inesquecível.',
    missionText: 'Nossa missão é simplificar o gerenciamento de playlists de karaoke e criar uma conexão autêntica entre organizadores e cantores, tornando cada evento memorável e bem organizado.',
    features: 'Principais características',
    feature1Title: 'Gerenciamento de eventos',
    feature1Desc: 'Crie e gerencie facilmente seus eventos de karaoke com todos os detalhes necessários.',
    feature2Title: 'Participação interativa',
    feature2Desc: 'Permita que os cantores se juntem aos seus eventos via código QR e votem em suas músicas favoritas.',
    feature3Title: 'Playlist em tempo real',
    feature3Desc: 'Visualize e gerencie a fila de músicas ao vivo com atualizações instantâneas.',
    feature4Title: 'Estatísticas detalhadas',
    feature4Desc: 'Analise o desempenho de seus eventos com gráficos e métricas em tempo real.',
    feature5Title: 'Segurança aprimorada',
    feature5Desc: 'Seus dados são protegidos por altos padrões de segurança e infraestrutura em nuvem robusta.',
    feature6Title: 'Suporte multilíngue',
    feature6Desc: 'KaraoNow suporta 8 idiomas para atingir um público global.',
    technology: 'Stack tecnológico',
    frontend: 'Frontend',
    backend: 'Backend',
    version: 'Versão atual',
    versionNote: 'Atualizada regularmente com novos recursos',
    visitWebsite: 'Site oficial',
    support: 'Suporte',
    supportNote: 'Sinta-se à vontade para nos contatar com dúvidas',
    copyright: 'Todos os direitos reservados.',
    privacyNotice: 'Verifique nossas páginas legais para saber mais sobre proteção de dados.',
    tryForFree: 'Comece seu teste gratuito',
  },
  // Cookies policy page
  cookiesPage: {
    title: 'Política de Cookies',
    subtitle: 'Informações completas sobre cookies usados no KaraoNow',
    overview: 'Visão Geral',
    overviewText: 'KaraoNow usa cookies para aprimorar sua experiência de usuário, proteger sua conta e entender como você usa nossa plataforma. Esta política explica como e por que usamos cookies.',
    whatAreCookies: 'O que são Cookies?',
    whatAreCookiesText: 'Cookies são pequenos arquivos de texto armazenados em seu dispositivo. Eles permitem que sites se lembrem de informações sobre suas visitas, como suas preferências e dados de login.',
    typesOfCookies: 'Tipos de Cookies',
    sessionCookies: 'Cookies de Sessão',
    sessionCookiesText: 'Esses cookies expiram quando você fecha seu navegador. São usados para manter sua sessão ativa e proteger seu acesso.',
    persistentCookies: 'Cookies Persistentes',
    persistentCookiesText: 'Esses cookies permanecem armazenados em seu dispositivo por um período definido. Eles nos permitem reconhecê-lo em suas próximas visitas.',
    howWeUseCookies: 'Como Usamos Cookies',
    useAuthentication: 'Autenticação e segurança de sessão',
    usePreferences: 'Lembrança de suas preferências (idioma, tema)',
    useSecurity: 'Proteção contra abuso e ataques',
    usePerformance: 'Melhoria do desempenho da plataforma',
    yourRights: 'Seus Direitos',
    yourRightsText: 'Você tem o direito de controlar os cookies. Você pode aceitar ou recusar cookies não essenciais através de nosso banner de consentimento.',
    managingCookies: 'Gerenciando Cookies',
    managingCookiesText: 'Você pode gerenciar cookies através das configurações do seu navegador. Observe que desabilitar certos cookies pode afetar o funcionamento do KaraoNow.',
    contact: 'Contato',
    contactText: 'Para qualquer dúvida sobre esta política, entre em contato através da nossa página de contato ou termos de serviço.',
    footer: 'Tem dúvidas? Volte para casa.',
  },
  // Singer menu
  singerMenu: {
    about: 'Sobre',
    dataPrivacy: 'Dados Pessoais',
  },
  // Organizer
  organizer: {
    // Dashboard
    dashboard: 'Painel de controle',
    dashboardSubtitle: 'Gerencie seus eventos de karaokê',
    menu: 'Menu do organizador',
    // Settings
    settings: 'Configurações',
    settingsSubtitle: 'Gerencie seu perfil e configurações',
    editProfile: 'Editar meu perfil',
    saveChanges: 'Salvar alterações',
    profileUpdated: 'Perfil atualizado com sucesso',
    emailChangeConfirmTitle: 'Modificação do endereço de email',
    emailChangeConfirmMessage: 'Um email de confirmação será enviado para o seu novo endereço de email. Seu novo email só será efetivo após você validar o link nesse email. Seu email antigo permanece ativo até a validação.',
    changePassword: 'Alterar senha',
    changePasswordDescription: 'Enviaremos um link para redefinir sua senha por email.',
    sendResetLink: 'Enviar link de redefinição',
    passwordResetLinkSent: 'Um link de redefinição de senha foi enviado para seu email.',
    accountData: 'Dados da conta',
    exportData: 'Exportar meus dados',
    exportDataDescription: 'Baixe uma cópia de todos os seus dados pessoais em formato JSON.',
    downloadData: 'Baixar meus dados',
    dataExported: 'Dados exportados com sucesso',
    dangerZone: 'Zona de perigo',
    deleteAccountDescription: 'Excluir sua conta é irreversível. Todos os seus dados serão excluídos permanentemente.',
    deleteAccount: 'Excluir minha conta',
    deleteAccountConfirm: 'Excluir',
    confirmDeleteAccount: 'Tem certeza de que deseja excluir sua conta? Esta ação é irreversível.',
    // Plan section
    planSection: 'Minha Assinatura',
    planSectionDescription: 'Veja seu plano atual, limites e opções de atualização',
    renewDate: 'Renovação em ',
    viewPlanDetails: 'Ver detalhes do plano',
    eventsLimit: 'Eventos criados (excluindo Evento Único)',
    subscriptionStatus: 'Status',
    statusActive: 'Ativo',
    statusInactive: 'Inativo',
    statusScheduledEnd: 'Fim Programado',
    subscriptionExpires: 'Expira em',
    subscriptionRenewsOn: 'Renova em',
    cancellationConfirmed: 'Cancelamento Confirmado!',
    continueAccessUntil: 'Você continua aproveitando seu plano Premium até {{date}}',
    autoDowngradeToFree: 'Você será automaticamente transferido para o plano Gratuito após essa data.',
    accessUntilEndOfPeriod: 'Você mantém o acesso até o final do seu período de cobrança atual.',
    planFeatures: 'Recursos do plano',
    featureEvents: 'Eventos por mês',
    featureSongsPerEvent: 'Músicas por evento',
    unlimited: 'Ilimitado',
    upgradePlan: 'Atualizar meu plano',
    managePlan: 'Gerenciar minha assinatura',
  },
  // Email change
  email: {
    changeEmail: 'Mudar meu endereço de email',
    changeEmailDescription: 'Para mudar seu endereço de email, você deve primeiro confirmar o novo endereço. Um email de confirmação será enviado para o novo endereço.',
    currentEmail: 'Email atual',
    newEmail: 'Novo endereço de email',
    newEmailAddress: 'Novo endereço',
    requestEmailChange: 'Solicitar mudança de email',
    emailRequired: 'O endereço de email é obrigatório',
    emailUnchanged: 'O novo endereço de email deve ser diferente do atual',
    emailChangeConfirmationSent: 'Um email de confirmação foi enviado para seu novo endereço de email.',
    emailChangeInfo: 'Seu email antigo permanece ativo até que o novo endereço seja validado.',
    verifying: 'Verificando',
    verifyingDescription: 'Verificando seu endereço de email...',
    success: 'Email confirmado',
    confirmationSuccess: 'Seu endereço de email foi alterado com sucesso!',
    confirmationFailed: 'Falha na confirmação',
    linkExpired: 'Link expirado',
    tokenExpired: 'O link de confirmação expirou. Por favor, solicite um novo.',
    linkExpiredDescription: 'O link de confirmação expirou (válido por 24 horas). Por favor, solicite um novo link de confirmação.',
    redirecting: 'Redirecionando...',
    redirectingToLogin: 'Redirecionando para o login em 3 segundos...',
    goToSettings: 'Ir para configurações',
    backToSettings: 'Voltar para configurações',
    requestNewLink: 'Solicitar um novo link',
    missingToken: 'Token de confirmação ausente.',
    validationPending: 'Validação pendente',
    pendingEmailAddress: 'Aguardando validação',
  },
  // RGPD - Privacidade de Dados
  rgpd: {
    pageTitle: 'Dados Pessoais',
    pageSubtitle: 'Gerencie seus dados e conta com segurança',
    exportData: 'Baixar Meus Dados',
    exportDataDescription: 'Baixe uma cópia completa dos seus dados pessoais em formato JSON. Você pode usar esses dados para importá-los em outro lugar ou simplesmente mantê-los como cópia de segurança.',
    exportIncludes: 'Esta exportação inclui',
    exportProfile: 'Perfil e preferências',
    exportHistory: 'Histórico de eventos',
    exportEvents: 'Eventos criados',
    exportSubscription: 'Informações de assinatura',
    downloadData: 'Baixar (JSON)',
    exporting: 'Baixando...',
    exportFormatJSON: 'Formato: JSON (compatível com LGPD)',
    dataExportedSuccess: '✅ Seus dados foram baixados com sucesso!',
    deleteAccount: 'Deletar Minha Conta',
    deleteAccountDescription: 'Esta ação deletará permanentemente sua conta e todos os dados associados. Esta operação não pode ser desfeita.',
    deleteWarning: '⚠️ Aviso Importante',
    deleteIrreversible: 'Esta ação é permanente e irreversível',
    deleteAllData: 'Todos os seus dados serão deletados',
    deleteNoRecovery: 'Não haverá possibilidade de recuperação',
    deleteRecommendation: 'Recomendamos que você baixe seus dados antes de deletar sua conta.',
    deleteAccountButton: 'Deletar Minha Conta',
    confirmDeletion: 'Confirmar deleção',
    deleteConfirmMessage: 'Tem certeza de que deseja deletar sua conta? Esta ação não pode ser desfeita.',
    continueDelete: 'Continuar para a deleção',
    finalDeleteWarning: 'Confirmação Final - Sem Volta',
    finalDeleteMessage: 'Esta é sua última chance! Clicar em "Deletar Permanentemente" deletará sua conta de forma irreversível.',
    permanentlyDelete: 'Deletar Permanentemente',
    gdprCompliance: 'Conformidade com LGPD',
    gdprInfo: 'De acordo com as regulamentações de LGPD, você tem o direito de acessar, retificar, deletar e portar seus dados pessoais. Essas ferramentas permitem que você exerça esses direitos.',
  },
  // Cookies consent
  cookies: {
    title: '🍪 Usamos cookies',
    description: 'Usamos cookies para melhorar sua experiência. Consulte nossa política para mais informações.',
    understood: 'Entendi',
    learnMore: 'Saiba mais',
    footerNote: 'Você pode consultar nossa Política de cookies para mais detalhes.',
  },
  // Footer
  footer: {
    legal: 'Aviso legal',
    privacy: 'Política de privacidade',
    cookies: 'Política de cookies',
    terms: 'Termos e Condições',
  },
  // Página de aviso legal
  legalPage: {
    title: 'Aviso Legal',
    subtitle: 'Informações legais sobre KaraoNow',
    editor: {
      title: 'Editor do site',
      name: 'axialdata',
      address: 'Impasse du pélican, 97233 Schoelcher, Martinica',
      phone: 'Telefone: +596696367741',
      email: 'Email: contact@axialdata.net',
      siret: 'SIRET: 49347625300023',
    },
    publication: {
      title: 'Diretor de publicação',
      director: 'Pierre-Yves Léglise',
    },
    hosting: {
      title: 'Hospedagem',
    },
    dataProtection: {
      title: 'Proteção de dados',
      description: 'Para obter mais informações sobre a proteção de seus dados pessoais, consulte nossa política de privacidade.',
      link: 'Leia nossa política de privacidade',
    },
  },
  // Página de política de privacidade
  privacyPage: {
    title: 'Política de Privacidade',
    subtitle: 'Como protegemos seus dados pessoais',
    lastUpdated: 'Última atualização: 1º de janeiro de 2025',
    intro: {
      title: 'Introdução',
      description: 'KaraoNow se compromete em proteger sua privacidade. Esta política explica como coletamos, usamos e protegemos seus dados pessoais em conformidade com a LGPD.',
    },
    dataCollection: {
      title: 'Dados coletados',
      organizers: {
        title: 'Para organizadores',
        email: 'Endereço de email',
        name: 'Nome, sobrenome e organização',
        organization: 'Detalhes da organização',
        payment: 'Informações de pagamento (via processador de terceiros)',
        events: 'Informações sobre eventos criados',
      },
      singers: {
        title: 'Para cantores',
        email: 'Endereço de email (opcional)',
        pseudo: 'Pseudônimo do usuário',
        name: 'Nome e sobrenome (opcional)',
        participation: 'Eventos em que você participou',
        history: 'Histórico de músicas adicionadas à fila',
      },
    },
    purposes: {
      title: 'Uso de seus dados',
      authentication: 'Autenticação e gerenciamento de conta',
      eventManagement: 'Criação e gerenciamento de eventos',
      songManagement: 'Gerenciamento de filas de música',
      communication: 'Comunicações importantes sobre sua conta',
      billing: 'Processamento de pagamentos e faturamento',
      support: 'Atendimento ao cliente e suporte técnico',
      preferences: 'Melhorar sua experiência do usuário',
    },
    legalBasis: {
      title: 'Base legal do processamento',
      description: 'O processamento de seus dados é baseado em seu consentimento ao se registrar e na execução do contrato de serviço entre você e KaraoNow.',
    },
    retention: {
      title: 'Período de retenção de dados',
      active: 'Dados ativos: retidos enquanto sua conta estiver ativa',
      inactive: 'Dados inativos: excluídos 12 meses após o último login',
      legal: 'Dados legais: retidos enquanto exigido por lei',
    },
    rights: {
      title: 'Seus direitos de LGPD',
      description: 'Sob a LGPD, você tem os seguintes direitos:',
      access: 'Direito de acessar seus dados pessoais',
      rectification: 'Direito de retificar seus dados',
      deletion: 'Direito ao esquecimento (exclusão de dados)',
      portability: 'Direito à portabilidade de dados',
      opposition: 'Direito de se opor ao processamento',
      limitation: 'Direito de limitar o processamento',
      contact: 'Para exercer esses direitos, entre em contato conosco em support@karaonow.com',
    },
    security: {
      title: 'Segurança',
      description: 'Usamos medidas de segurança apropriadas para proteger seus dados contra acesso não autorizado, modificação ou divulgação.',
    },
    cookies: {
      title: 'Cookies',
      description: 'KaraoNow usa cookies para melhorar sua experiência do usuário. Para obter mais informações, consulte nossa política de cookies.',
      link: 'Leia nossa política de cookies',
    },
    contact: {
      title: 'Entre em contato',
      description: 'Se tiver dúvidas sobre esta política ou seus dados pessoais, entre em contato conosco:',
      email: 'support@karaonow.com',
    },
  },
  // Página de termos de serviço
  termsPage: {
    title: 'Termos e Condições',
    subtitle: 'Termos e Condições do KaraoNow',
    lastUpdated: 'Última atualização: 1º de janeiro de 2025',
    intro: {
      title: 'Introdução',
      description: 'Bem-vindo ao KaraoNow! Ao usar nossa plataforma, você aceita estes termos e condições. Por favor, leia-os cuidadosamente.',
    },
    service: {
      title: 'Descrição do serviço',
      description: 'KaraoNow é uma plataforma SaaS para gerenciar eventos de karaokê. A plataforma oferece duas interfaces principais:',
      organizers: {
        title: 'Para organizadores',
        description: 'Os organizadores podem criar eventos, gerar códigos QR para compartilhar com cantores, gerenciar filas de música e modificar a ordem de turno. Um sistema de assinatura oferece três planos: gratuito (limitado), evento único e premium (assinatura mensal).',
      },
      singers: {
        title: 'Para cantores',
        description: 'Os cantores podem participar de eventos via código QR, adicionar músicas à fila do evento e visualizar a ordem das músicas. Os cantores podem participar sem conta (modo convidado) ou com uma conta para recursos avançados.',
      },
    },
    accounts: {
      title: 'Contas de usuário',
      description: 'Para usar KaraoNow, você deve criar uma conta. Você é responsável por manter a confidencialidade de suas credenciais.',
      responsibility: 'Você é responsável por todas as atividades realizadas através de sua conta',
      accuracy: 'Você concorda em fornecer informações precisas e atualizadas',
      security: 'Você concorda em manter a segurança de sua senha',
      notTransferable: 'Sua conta é pessoal e intransferível',
    },
    subscription: {
      title: 'Planos de assinatura',
      description: 'KaraoNow oferece vários planos de assinatura para organizadores:',
      free: 'Plano gratuito: Máximo de 4 eventos (todos os estados combinados, excluindo eventos únicos), 15 músicas por evento',
      oneShot: 'Evento único: Pagamento único para um evento específico, músicas ilimitadas, período de validade limitado',
      premium: 'Planos pagos com assinatura mensal: Vários planos disponíveis com cotas mensais de eventos, músicas ilimitadas por evento, renovação automática mensal',
    },
    payment: {
      title: 'Termos de pagamento',
      description: 'O pagamento é processado através de um processador de pagamento seguro de terceiros.',
      currency: 'Os pagamentos são feitos em euros (EUR)',
      noRefund: 'Nenhum reembolso é concedido para serviços já fornecidos',
      cancellation: 'Você pode cancelar sua assinatura a qualquer momento',
      automaticRenewal: 'As assinaturas mensais se renovam automaticamente a cada mês, a menos que sejam canceladas',
    },
    conduct: {
      title: 'Comportamento do usuário',
      description: 'Você concorda em usar o KaraoNow de forma legal e ética.',
      prohibited: {
        title: 'Ações proibidas',
        illegal: 'Qualquer atividade ilegal ou violação das leis aplicáveis',
        harmful: 'Conteúdo prejudicial, ofensivo ou difamatório',
        harassing: 'Assédio, intimidação ou discriminação',
        spam: 'Envio de spam ou outras comunicações indesejadas',
        fraud: 'Tentativas de fraude ou golpes',
      },
    },
    organizerResponsibilities: {
      title: 'Responsabilidades do organizador',
      description: 'Os organizadores aceitam as seguintes responsabilidades:',
      songSelection: 'Você é responsável pela legalidade do conteúdo das músicas',
      eventManagement: 'Você deve gerenciar seu evento de acordo com estes termos',
      dataManagement: 'Você é responsável pelo gerenciamento dos dados dos cantores',
      copyrightCompliance: 'Você concorda em cumprir com as leis de direitos autorais das músicas',
    },
    singerRights: {
      title: 'Direitos e responsabilidades do cantor',
      description: 'Os cantores têm os seguintes direitos e aceitam as seguintes responsabilidades:',
      participation: 'Você pode participar de eventos organizados por outros',
      songSelection: 'Você pode adicionar músicas à fila do evento',
      noDuplication: 'Você não pode adicionar a mesma música várias vezes',
      respect: 'Você concorda em respeitar as regras estabelecidas pelo organizador',
    },
    intellectualProperty: {
      title: 'Propriedade intelectual',
      description: 'Todos os direitos de propriedade intelectual do KaraoNow e seu conteúdo (exceto as músicas) pertencem ao KaraoNow. Você não pode reproduzir ou usar o conteúdo sem permissão.',
    },
    liability: {
      title: 'Limitação de responsabilidade',
      description: 'O KaraoNow é fornecido "como está". Não somos responsáveis por danos diretos ou indiretos resultantes do uso da plataforma. Nossa responsabilidade é limitada ao máximo do valor que você pagou pela sua assinatura no último mês.',
    },
    termination: {
      title: 'Término',
      description: 'KaraoNow se reserva o direito de suspender ou encerrar sua conta em caso de violação destes termos. Você pode encerrar sua conta a qualquer momento.',
    },
    modifications: {
      title: 'Modificações nos termos',
      description: 'KaraoNow se reserva o direito de modificar estes termos a qualquer momento. As modificações entram em vigência 30 dias após a notificação. Seu uso contínuo da plataforma após este período significa sua aceitação dos novos termos.',
    },
    jurisdiction: {
      title: 'Jurisdição',
      description: 'Estes termos são regidos pela lei francesa. Qualquer disputa estará sujeita aos tribunais competentes na França.',
    },
    contact: {
      title: 'Contato',
      description: 'Para qualquer pergunta sobre estes termos, entre em contato conosco:',
      email: 'support@karaonow.com',
    },
  },
}
